<template>
  <div>
    <div>
      <FilterCard :applied-filter-count="appliedFilterCount">
        <b-row>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Debit Note No"
              label-for="v-debit-note-no"
            >
              <b-form-input
                id="v-debit-note-no"
                v-model="filters['debit_note_no']"
                placeholder="Search"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Merchant"
              label-for="merchant_name"
            >
              <v-select
                v-model="filters['merchant_id']"
                :options="merchantOptions"
                :reduce="option => option.id"
                label="name"
                placeholder="Select Merchant"
                @input="handleMerchantChange"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Merchant Business"
              label-for="merchant_business"
            >
              <v-select
                id="merchant_business"
                v-model="filters['merchant_business_id']"
                :options="merchantBusinessOptions"
                :reduce="option => option.id"
                label="business_name"
                placeholder="Select Merchant Business"
                :disabled="!filters['merchant_id']"
              >
                <template v-slot:option="option">
                  {{ option.business_name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Status"
              label-for="debit_note_status"
            >
              <v-select
                v-model="filters['status']"
                :options="statusOptions"
                :reduce="option => option.key"
                label="name"
                placeholder="Select Status"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Debit Note Date"
              label-for="Debit-Note-Date"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['created_at']"
                  :opens="'right'"
                  @update="updateDateRange('created_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1 justify-content-end">
          <b-col cols="auto">
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </FilterCard>
    </div>
    <div>
      <b-card
        no-body
      >
        <div
          class="m-2"
        >
          <b-row class="d-sm-flex align-items-start">
            <b-col
              md="12"
              lg="8"
              class="d-block d-sm-flex flex-wrap justify-content-start align-items-center mb-1 mb-md-0"
            >
              <b-button
                v-can="'merchant-finance-paid'"
                :disabled="debitNoteSelected.length === 0"
                class="mr-1 add-margin-bottom"
                variant="success"
                @click="onClickMakePayment()"
              >
                Make Payment
              </b-button>
              <b-button
                v-can="'merchant-finance-approve'"
                :disabled="debitNoteSelected.length === 0"
                class="mr-1 add-margin-bottom"
                variant="info"
                @click="onClickApprove()"
              >
                Approve
              </b-button>
              <b-button
                v-can="'merchant-finance-reject'"
                :disabled="debitNoteSelected.length === 0"
                class="mr-1 add-margin-bottom"
                variant="danger"
                @click="onClickReject()"
              >
                Reject
              </b-button>
              <b-button
                v-can="'merchant-finance-create'"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                class="custom-button-color add-margin-bottom"
                @click="onClickNewDebitNote()"
              >
                <feather-icon icon="PlusIcon" />
                Add New Debit Note
              </b-button>
            </b-col>
            <b-col
              class="d-flex justify-content-end align-items-center mb-1 mb-md-0"
            >
              <ReportTableButtons
                :items="items"
                :json_fieldz="json_fields"
                :name="`MerchantDebitNotes - Page ${page}.xls`"
                bulkname="MerchantDebitNotes.xls"
                :fetch="fetchDebitNoteDataNoPagination"
                @refresh="onClickRefresh"
              />
            </b-col>
          </b-row>
          <b-row>
            <div
              class="font-weight-bolder text-muted ml-1 mt-1"
            >
              {{ `${debitNoteSelected.length} record${debitNoteSelected.length === 1 ? '':'s'}` }} selected
            </div>
          </b-row>
        </div>

        <!-- table -->
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            hover
            responsive
            striped
            show-empty
            sticky-header="100vh"
            class="hide-vertical-scroll"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #head(id)>
              <div class="d-flex align-items-start">
                <div>
                  <b-form-checkbox
                    id="selectAllCheckbox"
                    v-model="selectAllCheckbox"
                    name="selectAllCheckbox"
                    @change="onClickSelectAllCheckbox()"
                  />
                </div>
              </div>
            </template>
            <template #cell(id)="data">
              <div class="d-flex align-items-start">
                <div>
                  <div class="font-weight-bolder">
                    <b-form-checkbox
                      v-model="debitNoteSelected"
                      :value="data.item.id"
                    />
                  </div>
                </div>
              </div>
            </template>
            <template #cell(merchant.name)="data">
              <div class="d-flex align-items-start">
                <div>
                  <div class="">
                    {{ `${data.value}${data.item.merchant.merchant_setting.merchant_type === 'cash' ? "&nbsp;[&nbsp;C&nbsp;]" : ''}` }}
                  </div>
                </div>
              </div>
            </template>
            <template #cell(merchant_business)="data">
              <span>{{ data.item.merchant_business || '---' }}</span>
            </template>
            <template #cell(debit_note_no)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  :text="avatarText(data.item.debit_note_no)"
                  size="32"
                  variant="light-primary"
                />
                <div class="ml-1">
                  <div
                    class="font-weight-bolder text-primary"
                    @click="onClickDebitNote(data.item.id)"
                  >
                    {{ data.value }}
                  </div>
                  <div class="font-small-2">
                    {{ __dateTimeFormatter(data.item.created_at) }}
                  </div>
                </div>
              </div>
            </template>
            <template #cell(commission_rate)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(commission)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(delivery_rate)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(delivery_charge)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(return_rate)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(return_charge)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(debit_amount)="data">
              <span>{{ __numberWithCommas(data.value, true) }}</span>
            </template>
            <template #cell(deposited_date)="data">
              {{ __dateTimeFormatter(data.value) || '---' }}
            </template>
            <template #cell(approved_date)="data">
              {{ __dateTimeFormatter(data.value) || '---' }}
            </template>
            <template #cell(finance_current_status.key)="data">
              <finance-status-badge
                :status-key="data.value"
                :finance-status="statusOptions"
              />
              <div
                v-if="data.item.is_reversed_debit_note"
                class="mt-1 d-flex justify-content-center"
              >
                <b-tooltip
                  :target="data.item.id.toString()"
                  triggers="hover"
                  variant="danger"
                >
                  Rejected Once
                </b-tooltip>
                <b-avatar
                  :id="data.item.id.toString()"
                  size="20"
                  style="background-color: #ffb5b5"
                >
                  <feather-icon
                    icon="ChevronsLeftIcon"
                    class="text-body align-middle"
                  />
                </b-avatar>
              </div>
            </template>
            <template #cell(action)="data">
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                :class="items.length !== 1 ? 'dropdown-no-scroll':''"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  :to="{ name: 'merchant-debit-note-preview', params: { id: data.item.id } }"
                >
                  <feather-icon icon="BookIcon" />
                  View Debit Note
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="data.item.receipt_url"
                  :href="`${bucketUrl}${data.item.receipt_url}`"
                  target="_blank"
                >
                  <feather-icon icon="EyeIcon" />
                  View Payment Slip
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="data.item.is_reversed_debit_note"
                  @click="onClickShowRejectionReasons(data.item.id)"
                >
                  <feather-icon icon="BookIcon" />
                  Rejection Reasons
                </b-dropdown-item>
                <b-dropdown-item
                  v-can="'merchant-finance-show'"
                  :to="{ name: 'merchant-debit-note-invoices', params: { id: data.item.id } }"
                >
                  <feather-icon icon="ArchiveIcon" />
                  View Invoices
                </b-dropdown-item>
                <b-dropdown-item

                  @click="onClickOpenRequestPaymentModal(data.item.id)"
                >
                  <feather-icon icon="PocketIcon" />
                  View Payment Requests
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </b-overlay>

        <div class="mx-2 mb-2">
          <b-row
            cols="12"
          >
            <b-col
              class="d-flex align-items-center justify-content-sm-start"
              md="3"
            >
              <label class="width-75">Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              class="d-flex align-items-center justify-content-sm-end"
              md="9"
            >
              <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total)? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>
      <request-payment ref="merchant_payment_request_modal" />
    </div>
    <make-deposite-action ref="make_deposit_action_modal" />
    <approve-action ref="approve_action_modal" />
    <reject-action ref="reject_action_modal" />
    <rejection-details ref="rejection_detail_modal" />
  </div>
</template>

<script>
import {
  BFormCheckbox,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormInput,
  BPagination,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BAvatar,
  VBTooltip,
  BTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { bucketUrl } from '@/constants/config'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@core/utils/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import FinanceStatusBadge from '@core/components/status/FinanceStatusBadge.vue'
import store from '@/store/index'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import MakeDepositeAction from '@/components/FinanceModuleForms/ActionForms/MakeDepositeAction.vue'
import ApproveAction from '@/components/FinanceModuleForms/ActionForms/ApproveAction.vue'
import RejectAction from '@/components/FinanceModuleForms/ActionForms/RejectAction.vue'
import requestPayment from '@/components/RequestPayment/requestPayment.vue'
import RejectionDetails from '@/components/FinanceModuleForms/InfoModals/RejectionDetailModal.vue'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'

const FinanceRepository = RepositoryFactory.get('finance')
const MerchantRepository = RepositoryFactory.get('merchant')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    MakeDepositeAction,
    ApproveAction,
    RejectAction,
    vSelect,
    AdvancedDateFilter,
    BTooltip,
    BOverlay,
    BCard,
    BTable,
    BAvatar,
    BFormCheckbox,
    BRow,
    BCol,
    BFormGroup,
    BPagination,
    BButton,
    BFormInput,
    BDropdown,
    BDropdownItem,
    requestPayment,
    FinanceStatusBadge,
    RejectionDetails,
    FilterButtons,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      bucketUrl,
      debitNoteSelected: [],
      merchantOptions: [],
      merchantBusinessOptions: [],
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
        return_charge: '',
      },
      dataLoading: false,
      sort: '',
      avatarText,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      totalRows: 1,
      page: 1,
      meta: {},
      dir: false,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      infoModal: {
        id: 'info-modal',
        title: '',
        content: '',
      },
      fields: [
        {
          key: 'id',
          sortable: false,
        },
        {
          key: 'debit_note_no',
          label: 'Debit Note',
          sortable: true,
        },
        {
          key: 'merchant.ref_no',
          label: 'Merchant ref no',
          sortable: true,
        },
        {
          key: 'merchant.name',
          label: 'Merchant',
          sortable: true,
        },
        {
          key: 'merchant_business',
          label: 'Merchant Business',
          sortable: true,
        },
        {
          key: 'commission_rate',
          label: 'Commission Rate',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'commission',
          label: 'Commission',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'delivery_rate',
          label: 'Delivery Charge Rate',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'delivery_charge',
          label: 'Delivery Charge',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'return_rate',
          label: 'Return Charge Rate',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'return_charge',
          label: 'Return Charge',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'debit_amount',
          label: 'Debit Note Amount',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'deposited_date',
          label: 'Paid Date',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          key: 'approved_date',
          label: 'Approved Date',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          key: 'finance_current_status.key',
          label: 'Status',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        { key: 'action', label: 'Action', sortable: false },
      ],
      json_fields: {
        'Debit Note NO': 'debit_note_no',
        'Created Date': {
          field: 'created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Merchant Reference Number': {
          field: 'merchant.ref_no',
          callback: value => `"${value}"`,
        },
        Merchant: {
          field: 'merchant.name',
          callback: value => `"${value}"`,
        },
        'Merchant Business': {
          field: 'merchant_business',
          callback: value => `"${value}"`,
        },
        'Commission Rate': 'commission_rate',
        Commission: 'commission',
        'Delivery Charge Rate': 'delivery_rate',
        'Delivery Charge': 'delivery_charge',
        'Return Charge Rate': 'return_rate',
        'Return Charge': 'return_charge',
        'Debit Note Amount': 'debit_amount',
        'Paid Date': {
          field: 'deposited_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Approved Date': {
          field: 'approved_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        Status: 'finance_current_status.name',
      },
      items: [],
      statusOptions: [],
      debitNoteTypeOptions: [
        {
          name: 'Payable',
          key: 'payable',
        },
        {
          name: 'Receivable',
          key: 'receivable',
        },
      ],
      selectAllCheckbox: false,
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchDebitNoteData()
      },
    },
    deep: true,
    page() {
      this.fetchDebitNoteData()
    },
    perPage() {
      this.fetchDebitNoteData()
    },
  },
  async mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length
    this.statusOptions = JSON.parse(localStorage.getItem('finance_statuses'))
    this.getMerchantList()
    this.setMonthFilter()
    this.fetchDebitNoteData()
    this.updateFilterCount()
  },
  methods: {
    setMonthFilter() {
      const currentRange = this.filters?.created_at
      if (currentRange?.startDate || currentRange?.endDate) return

      const currentMonth = this.monthFilter()
      this.updateDateRange('created_at', currentMonth)
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await MerchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
    },
    async getMerchantBusinessList() {
      try {
        if (!this.filters.merchant_id) {
          this.merchantBusinessOptions = []
          this.$set(this.filters, 'merchant_business_id', null)
          return
        }
        const { data } = (await MerchantRepository.getMerchantBusinessListDropdown(this.filters.merchant_id)).data
        this.merchantBusinessOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    handleMerchantChange() {
      this.$set(this.filters, 'merchant_business_id', null)
      this.getMerchantBusinessList()
    },
    async fetchDebitNoteData() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await FinanceRepository.getMerchantDebitNoteList(this.page, filterQuery, this.perPage, this.sort))
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchDebitNoteDataNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await FinanceRepository.getMerchantDebitNoteListNoPagination(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`
      this.infoModal.content = JSON.stringify(item, null, 2)
      this.$root.$emit('bv::show::modal', this.infoModal.id, button)
    },
    handleChangePage(page) {
      this.page = page
      this.fetchDebitNoteData()
    },
    sortChanged(e) {
      const key = e.sortBy
      if (!key) {
        this.sort = ''
        this.fetchDebitNoteData()
        return
      }

      const active = e.sortDesc ? '-' : ''
      this.sort = `${active}${key}`
      if (this.page !== 1) {
        this.page = 1
        return
      }
      this.fetchDebitNoteData()
    },
    applyFilters() {
      this.fetchDebitNoteData()
      this.deselectAll()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
        return_charge: '',
      }
      this.merchantBusinessOptions = []
      this.fetchDebitNoteData()
      this.updateFilterCount()
    },
    onClickOpenRequestPaymentModal(id) {
      this.$refs.merchant_payment_request_modal.openModal(id)
    },
    onClickShowRejectionReasons(id) {
      this.$refs.rejection_detail_modal.openModal(id, 'merchant-debit-note')
    },
    selectAll() {
      this.debitNoteSelected = this.items.map(n => n.id)
    },
    deselectAll() {
      this.debitNoteSelected = []
    },
    onClickMakePayment() {
      this.$refs.make_deposit_action_modal.openModal(this.debitNoteSelected, 'merchant-debit-note')
    },
    onClickApprove() {
      this.$refs.approve_action_modal.openModal(this.debitNoteSelected, 'merchant-debit-note')
    },
    onClickReject() {
      this.$refs.reject_action_modal.openModal(this.debitNoteSelected, 'merchant-debit-note')
    },
    onClickNewDebitNote() {
      this.$router.push({ name: 'add-new-merchant-debit-note' })
    },
    onClickRefresh() {
      this.fetchDebitNoteData()
      this.deselectAll()
    },
    onClickDebitNote(debitNote) {
      this.$router.push({
        name: 'merchant-debit-note-preview',
        params: {
          id: debitNote,
        },
      })
    },
    onClickSelectAllCheckbox() {
      if (this.selectAllCheckbox) {
        // If the "Select All" checkbox is checked, select all waybill numbers.
        this.debitNoteSelected = this.items.map(n => n.id)
      } else {
        this.debitNoteSelected = []
      }
    },
  },
}

</script>
<style lang="scss">
@import '../../@core/scss/vue/libs/vue-select.scss';
@media (max-width: 767px) {
  .add-margin-bottom {
    width: 100%;
    margin-bottom: 20px;
  }
  .pagination-text {
    font-size: 0.75rem;
  }
}
</style>
